import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, Copy, Plus, Trash2 } from 'lucide-react';
import { FormDrawer } from './FormDrawer';
import { SearchableSelect } from './SearchableSelect';
import purchaseOrdersAPI from '../../services/purchaseOrdersAPI';
import { PO_ITEM_TYPE_ORDER } from '../placeholders/purchaseOrderSummary';

const FALLBACK_CURRENCY_OPTIONS = [
  { value: 'CLP', label: 'CLP' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];

const emptyCatalogItem = (itemType = 'Materias primas') => ({
  item_type: itemType || 'Materias primas',
  item_code: '',
  description: '',
  unit: 'UN',
  format_code: '',
  unit_price: 0,
});

const emptyLine = () => ({
  id: null,
  item_type: '',
  item_code: '',
  description: '',
  detail: '',
  quantity: 1,
  unit: 'UN',
  format_code: '',
  unit_price: 0,
  purchase_request_id: '',
});

const lineTotal = (item) => {
  const qty = Number(item?.quantity || 0);
  const price = Number(item?.unit_price || 0);
  return Math.round(qty * price * 100) / 100;
};

const formatMoney = (value, currency = 'CLP') => {
  const raw = String(currency || 'CLP').trim().toUpperCase() || 'CLP';
  const code = /^[A-Z]{3}$/.test(raw) ? raw : 'CLP';
  try {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: code === 'CLP' ? 0 : 2,
    }).format(Number(value || 0));
  } catch {
    return `${code} ${Number(value || 0).toLocaleString('es-CL')}`;
  }
};

const taxRateForDocument = (documentType) => {
  const label = String(documentType || '').toLowerCase();
  if (label.includes('exent') || label.includes('guia') || label.includes('guía')) return 0;
  return 0.19;
};

const ITEM_GRID =
  'minmax(120px, 1.1fr) minmax(100px, 0.9fr) minmax(80px, 0.75fr) minmax(140px, 1.5fr) minmax(140px, 1.5fr) 72px 80px 100px 90px 76px';

export const PurchaseOrdersForm = ({
  isOpen,
  onClose,
  onSave,
  onEmit,
  isSaving = false,
  isDarkMode = false,
  isEditing = false,
  formData,
  onChange,
  formError = '',
  supplierOptions = [],
  contactOptions = [],
  paymentTermOptions = [],
  documentTypeOptions = [],
  currencyOptions = [],
  itemTypeOptions = [],
  deliveryConditionOptions = [],
  buyerOptions = [],
  purchaseRequestOptions = [],
  catalogProducts = [],
  formatOptions = [],
  supplierRut = '',
  onCreateCatalogItem,
  onAddSupplier,
}) => {
  const items = Array.isArray(formData?.items) ? formData.items : [];
  const currency = formData?.currency || 'CLP';
  const resolvedCurrencyOptions =
    Array.isArray(currencyOptions) && currencyOptions.length > 0
      ? currencyOptions
      : FALLBACK_CURRENCY_OPTIONS;
  const currencySelectOptions = [
    ...resolvedCurrencyOptions,
    ...(currency && !resolvedCurrencyOptions.some((opt) => opt.value === currency)
      ? [{ value: currency, label: currency }]
      : []),
  ];
  const exchangeRate = Number(formData?.exchange_rate || 1) || 1;
  // Editable al crear, o al editar mientras siga en Borrador.
  const canEditPoCode =
    !isEditing || String(formData?.status || '').trim().toLowerCase() === 'borrador';
  const [showCatalogForm, setShowCatalogForm] = useState(false);
  const [catalogForm, setCatalogForm] = useState(() => emptyCatalogItem());
  const [catalogError, setCatalogError] = useState('');
  const [isSavingCatalog, setIsSavingCatalog] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShowCatalogForm(false);
      setCatalogError('');
    }
  }, [isOpen]);

  const totals = useMemo(() => {
    const subtotalOriginal = items.reduce((sum, item) => sum + lineTotal(item), 0);
    const subtotal = Math.round(subtotalOriginal * exchangeRate * 100) / 100;
    const taxRate = taxRateForDocument(formData?.document_type);
    const tax = Math.round(subtotal * taxRate * 100) / 100;
    const total = Math.round((subtotal + tax) * 100) / 100;
    const advance = Number(formData?.advance_amount || 0);
    const withholdings = Number(formData?.withholdings_amount || 0);
    const amountDue = Math.round((total - advance - withholdings) * 100) / 100;
    return { subtotalOriginal, subtotal, tax, total, advance, withholdings, amountDue };
  }, [items, exchangeRate, formData?.document_type, formData?.advance_amount, formData?.withholdings_amount]);

  const updateItem = (index, patch) => {
    const next = items.map((item, idx) => (idx === index ? { ...item, ...patch } : item));
    onChange('items', next);
  };

  const productsForType = (itemType) => {
    const type = String(itemType || '').trim().toLowerCase();
    const list = Array.isArray(catalogProducts) ? catalogProducts : [];
    if (!type) return list;
    return list.filter((product) => String(product.item_type || '').trim().toLowerCase() === type);
  };

  const handleItemTypeChange = (index, itemType) => {
    updateItem(index, {
      item_type: itemType,
      description: '',
      detail: '',
      item_code: '',
      unit: 'UN',
      format_code: '',
      unit_price: 0,
    });
  };

  const handleDescriptionChange = (index, description, product = null) => {
    const current = items[index] || {};
    const pool = productsForType(current.item_type);
    const match =
      product ||
      pool.find(
        (row) =>
          String(row.description || row.name || '').trim().toLowerCase() ===
          String(description || '').trim().toLowerCase()
      ) ||
      null;
    updateItem(index, {
      description,
      item_code: match?.sku || match?.code || (product ? product.sku || product.code || '' : ''),
      unit: match?.unit || match?.format || current.unit || 'UN',
      format_code: match?.format_code || current.format_code || '',
      unit_price: match?.unit_price ?? match?.unit_cost ?? current.unit_price ?? 0,
    });
  };

  const addItem = () => onChange('items', [...items, emptyLine()]);
  const removeItem = (index) => onChange('items', items.filter((_, idx) => idx !== index));
  const duplicateItem = (index) => {
    const source = items[index];
    if (!source) return;
    const next = [...items];
    next.splice(index + 1, 0, { ...source, id: null });
    onChange('items', next);
  };

  const catalogTypeOptions = itemTypeOptions.length
    ? itemTypeOptions
    : PO_ITEM_TYPE_ORDER.map((label) => ({ value: label, label }));

  const applyCatalogItemToLines = (created) => {
    const patch = {
      item_type: created?.item_type || catalogForm.item_type || '',
      item_code: created?.item_code || created?.sku || created?.code || '',
      description: created?.description || created?.name || '',
      unit: created?.unit || catalogForm.unit || 'UN',
      format_code: created?.format_code || catalogForm.format_code || '',
      unit_price: created?.unit_price ?? created?.unit_cost ?? catalogForm.unit_price ?? 0,
    };
    const emptyIndex = items.findIndex(
      (row) => !String(row.description || '').trim() && !String(row.item_code || '').trim()
    );
    if (emptyIndex >= 0) {
      updateItem(emptyIndex, patch);
      return;
    }
    onChange('items', [...items, { ...emptyLine(), ...patch }]);
  };

  const openCatalogForm = () => {
    const lastType =
      items[items.length - 1]?.item_type || catalogTypeOptions[0]?.value || 'Materias primas';
    setCatalogForm(emptyCatalogItem(lastType));
    setCatalogError('');
    setShowCatalogForm(true);
  };

  const handleCatalogFormChange = (key, value) => {
    setCatalogForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'unit') {
        const fmt = (formatOptions || []).find((opt) => opt.value === value || opt.label === value);
        if (fmt?.format_code) next.format_code = fmt.format_code;
      }
      return next;
    });
  };

  const handleSaveCatalogItem = async () => {
    const description = String(catalogForm.description || '').trim();
    const itemType = String(catalogForm.item_type || '').trim();
    if (!itemType) {
      setCatalogError('Selecciona un tipo.');
      return;
    }
    if (!description) {
      setCatalogError('El nombre del ítem es obligatorio.');
      return;
    }
    const payload = {
      item_type: itemType,
      description,
      item_code: String(catalogForm.item_code || '').trim(),
      unit: String(catalogForm.unit || 'UN').trim() || 'UN',
      format_code: String(catalogForm.format_code || '').trim(),
      unit_price: Number(catalogForm.unit_price || 0),
    };
    setIsSavingCatalog(true);
    setCatalogError('');
    try {
      const created = onCreateCatalogItem
        ? await onCreateCatalogItem(payload)
        : await purchaseOrdersAPI.createCatalogItem(payload);
      applyCatalogItemToLines(created || payload);
      setShowCatalogForm(false);
    } catch (err) {
      setCatalogError(err?.message || 'No se pudo crear el ítem en el catálogo.');
    } finally {
      setIsSavingCatalog(false);
    }
  };

  const iconButtonStyle = (colors, danger = false) => ({
    height: 34,
    width: 34,
    borderRadius: 8,
    border: `1px solid ${colors.inputBorder}`,
    background: 'transparent',
    color: danger ? colors.danger || '#dc2626' : colors.text,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    flexShrink: 0,
  });

  const sections = [
    {
      key: 'header',
      title: '1. Identificación de la OC',
      flat: true,
      columns: 3,
      fields: [
        {
          key: 'po_code',
          label: 'Código OC',
          disabled: !canEditPoCode,
          readOnly: !canEditPoCode,
          placeholder: canEditPoCode
            ? 'YYXXX (ej. 26001). Vacío = correlativo automático'
            : undefined,
        },
        { key: 'order_date', label: 'Fecha de la OC', type: 'date' },
        {
          key: 'buyer_name',
          label: 'Ejecutivo que emite la OC',
          type: 'select',
          options: [
            { value: '', label: 'Seleccionar ejecutivo' },
            ...buyerOptions,
            ...(formData?.buyer_name &&
            !buyerOptions.some((o) => o.value === formData.buyer_name)
              ? [{ value: formData.buyer_name, label: formData.buyer_name }]
              : []),
          ],
        },
      ],
    },
    {
      key: 'supplier',
      title: '2. Proveedor',
      flat: true,
      columns: 3,
      fields: [
        {
          key: 'supplier_id',
          label: 'Proveedor',
          type: 'searchable-select',
          placeholder: 'Escribe para buscar proveedor…',
          options: supplierOptions,
          onAdd: onAddSupplier || undefined,
          addButtonTitle: 'Nuevo proveedor',
        },
        {
          key: 'supplier_rut_display',
          label: 'RUT',
          type: 'custom',
          render: ({ colors, baseInputStyle }) => (
            <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
              RUT
              <input
                style={{ ...baseInputStyle, opacity: 0.85 }}
                value={supplierRut || ''}
                readOnly
                disabled
                placeholder="Se completa al elegir proveedor"
              />
            </label>
          ),
        },
        {
          key: 'supplier_contact_id',
          label: 'Contacto',
          type: 'select',
          options: [{ value: '', label: 'Seleccionar contacto' }, ...contactOptions],
        },
      ],
    },
    {
      key: 'document',
      title: '3. Documento y moneda',
      flat: true,
      columns: 3,
      fields: [
        {
          key: 'document_type',
          label: 'Tipo de documento',
          type: 'select',
          options: [{ value: '', label: 'Seleccionar' }, ...documentTypeOptions],
        },
        {
          key: 'currency',
          label: 'Moneda',
          type: 'select',
          options: currencySelectOptions,
        },
        {
          key: 'exchange_rate',
          label: 'Conversión',
          type: 'number',
          step: '0.0001',
        },
      ],
    },
    {
      key: 'delivery',
      title: '4. Condiciones de pago y entrega',
      flat: true,
      columns: 3,
      fields: [
        {
          key: 'payment_terms',
          label: 'Condición de pago',
          type: 'select',
          options: [
            { value: '', label: formData?.supplier_id ? 'Seleccionar' : 'Selecciona un proveedor primero' },
            ...paymentTermOptions,
            ...(formData?.payment_terms &&
            !paymentTermOptions.some((o) => o.value === formData.payment_terms)
              ? [{ value: formData.payment_terms, label: formData.payment_terms }]
              : []),
          ],
        },
        {
          key: 'delivery_conditions',
          label: 'Condiciones de entrega',
          type: 'select',
          options: [{ value: '', label: 'Seleccionar' }, ...deliveryConditionOptions],
        },
        { key: 'delivery_place', label: 'Lugar de entrega' },
      ],
    },
    {
      key: 'items',
      title: '5. Ítems',
      flat: true,
      columns: 1,
      fields: [
        {
          key: 'items',
          fullWidth: true,
          type: 'custom',
          render: ({ colors, baseInputStyle }) => {
            const inputStyle = {
              ...baseInputStyle,
              marginTop: 0,
              height: 34,
            };
            const codeInputStyle = {
              ...inputStyle,
              background: isDarkMode ? '#1e3a5f' : '#eff6ff',
              opacity: 1,
            };

            return (
              <div style={{ display: 'grid', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button
                    type="button"
                    onClick={openCatalogForm}
                    disabled={isSaving || isSavingCatalog}
                    style={{
                      height: 32,
                      borderRadius: 8,
                      border: 'none',
                      background: '#16a34a',
                      color: '#fff',
                      cursor: isSaving || isSavingCatalog ? 'not-allowed' : 'pointer',
                      padding: '0 10px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      opacity: isSaving || isSavingCatalog ? 0.65 : 1,
                    }}
                  >
                    <BookOpen size={14} /> Nuevo ítem catálogo
                  </button>
                  <button
                    type="button"
                    onClick={addItem}
                    style={{
                      height: 32,
                      borderRadius: 8,
                      border: `1px solid ${colors.inputBorder}`,
                      background: 'transparent',
                      color: colors.text,
                      cursor: 'pointer',
                      padding: '0 10px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <Plus size={14} /> Agregar ítem
                  </button>
                </div>

                {items.length > 0 ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: ITEM_GRID,
                      gap: 8,
                      alignItems: 'center',
                      padding: '0 2px',
                    }}
                  >
                    {['Solicitud', 'Tipo', 'Código', 'Item', 'Detalle', 'Cant.', 'Formato', 'P. unitario', 'Total', ''].map(
                      (label) => (
                        <span
                          key={label || 'actions'}
                          style={{
                            color: colors.muted,
                            fontSize: 11,
                            fontWeight: 600,
                            lineHeight: 1.2,
                          }}
                        >
                          {label}
                        </span>
                      )
                    )}
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: 12, color: colors.muted }}>
                    Sin ítems. Agrega al menos uno.
                  </p>
                )}

                {items.map((item, index) => {
                  const selectedRequestId = item.purchase_request_id
                    ? String(item.purchase_request_id)
                    : '';
                  const hasSelectedRequest =
                    selectedRequestId &&
                    purchaseRequestOptions.some((opt) => String(opt.value) === selectedRequestId);
                  return (
                    <div
                      key={item.id || `new-${index}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: ITEM_GRID,
                        gap: 8,
                        alignItems: 'start',
                      }}
                    >
                      <select
                        style={inputStyle}
                        value={selectedRequestId}
                        onChange={(e) =>
                          updateItem(index, {
                            purchase_request_id: e.target.value || '',
                          })
                        }
                        title="Solicitud de compra asociada"
                      >
                        <option value="">Sin solicitud</option>
                        {purchaseRequestOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                        {selectedRequestId && !hasSelectedRequest ? (
                          <option value={selectedRequestId}>
                            {item.purchase_request_code || `SOL #${selectedRequestId}`}
                          </option>
                        ) : null}
                      </select>

                      <select
                        style={inputStyle}
                        value={item.item_type || ''}
                        onChange={(e) => handleItemTypeChange(index, e.target.value)}
                      >
                        <option value="">Tipo</option>
                        {itemTypeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>

                      <input
                        style={codeInputStyle}
                        value={item.item_code || ''}
                        readOnly
                        disabled
                        placeholder="—"
                        title="Código según ítem"
                      />

                      <SearchableSelect
                        style={inputStyle}
                        value={item.description || ''}
                        allowFreeText
                        isDarkMode={isDarkMode}
                        placeholder="Escribe para buscar o crear…"
                        options={productsForType(item.item_type).map((p) => ({
                          value: p.description || p.name,
                          label: p.sku
                            ? `${p.description || p.name} · ${p.sku}`
                            : p.description || p.name,
                          sku: p.sku || p.code,
                          code: p.sku || p.code,
                          unit: p.unit || p.format,
                          format_code: p.format_code,
                          unit_price: p.unit_price ?? p.unit_cost,
                        }))}
                        onChange={(description, option) =>
                          handleDescriptionChange(index, description, option)
                        }
                      />

                      <input
                        style={inputStyle}
                        value={item.detail || ''}
                        onChange={(e) => updateItem(index, { detail: e.target.value })}
                        placeholder="Detalle libre…"
                        title="Detalle del ítem"
                      />

                      <input
                        type="number"
                        step="0.01"
                        style={inputStyle}
                        value={item.quantity ?? 1}
                        onChange={(e) => updateItem(index, { quantity: e.target.value })}
                      />

                      <select
                        style={inputStyle}
                        value={item.unit || ''}
                        onChange={(e) => {
                          const nextUnit = e.target.value;
                          const fmt = formatOptions.find(
                            (opt) => opt.value === nextUnit || opt.label === nextUnit
                          );
                          updateItem(index, {
                            unit: nextUnit,
                            format_code: fmt?.format_code || item.format_code || '',
                          });
                        }}
                      >
                        <option value="">Formato</option>
                        {(formatOptions || []).map((opt) => (
                          <option key={opt.format_code || opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                        {item.unit &&
                        !(formatOptions || []).some(
                          (opt) => opt.value === item.unit || opt.label === item.unit
                        ) ? (
                          <option value={item.unit}>{item.unit}</option>
                        ) : null}
                      </select>

                      <input
                        type="number"
                        step="0.01"
                        style={inputStyle}
                        value={item.unit_price ?? 0}
                        onChange={(e) => updateItem(index, { unit_price: e.target.value })}
                      />

                      <div
                        style={{
                          height: 34,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          fontSize: 12,
                          fontWeight: 700,
                          color: colors.text,
                          paddingRight: 2,
                        }}
                      >
                        {formatMoney(lineTotal(item), currency)}
                      </div>

                      <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                        <button
                          type="button"
                          onClick={() => duplicateItem(index)}
                          style={iconButtonStyle(colors)}
                          aria-label="Duplicar ítem"
                          title="Duplicar ítem"
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          style={iconButtonStyle(colors, true)}
                          aria-label="Eliminar ítem"
                          title="Eliminar ítem"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}

                <div
                  style={{
                    marginTop: 4,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <div
                    style={{
                      minWidth: 260,
                      display: 'grid',
                      gap: 4,
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: `1px solid ${colors.sectionBorder}`,
                      background: colors.panel || colors.inputBg || colors.sectionBg,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 24,
                        fontSize: 13,
                        color: colors.text,
                      }}
                    >
                      <span>Subtotal ({currency})</span>
                      <span style={{ fontWeight: 600 }}>{formatMoney(totals.subtotalOriginal, currency)}</span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 24,
                        fontSize: 13,
                        color: colors.text,
                      }}
                    >
                      <span>Total neto</span>
                      <span style={{ fontWeight: 600 }}>{formatMoney(totals.subtotal, 'CLP')}</span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 24,
                        fontSize: 13,
                        color: colors.text,
                      }}
                    >
                      <span>IVA</span>
                      <span style={{ fontWeight: 600 }}>{formatMoney(totals.tax, 'CLP')}</span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 24,
                        fontSize: 13,
                        color: colors.text,
                        paddingTop: 6,
                        borderTop: `1px solid ${colors.sectionBorder}`,
                        fontWeight: 700,
                      }}
                    >
                      <span>Total</span>
                      <span>{formatMoney(totals.total, 'CLP')}</span>
                    </div>
                    <label
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 16,
                        fontSize: 13,
                        color: colors.text,
                        marginTop: 2,
                      }}
                    >
                      <span>Anticipo</span>
                      <input
                        type="number"
                        step="0.01"
                        style={{ ...inputStyle, width: 120, textAlign: 'right' }}
                        value={formData?.advance_amount ?? 0}
                        onChange={(e) => onChange('advance_amount', e.target.value)}
                      />
                    </label>
                    <label
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 16,
                        fontSize: 13,
                        color: colors.text,
                      }}
                    >
                      <span>Retención</span>
                      <input
                        type="number"
                        step="0.01"
                        style={{ ...inputStyle, width: 120, textAlign: 'right' }}
                        value={formData?.withholdings_amount ?? 0}
                        onChange={(e) => onChange('withholdings_amount', e.target.value)}
                      />
                    </label>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 24,
                        fontSize: 13,
                        color: colors.text,
                        paddingTop: 6,
                        borderTop: `1px solid ${colors.sectionBorder}`,
                        fontWeight: 700,
                      }}
                    >
                      <span>A pagar</span>
                      <span>{formatMoney(totals.amountDue, 'CLP')}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          },
        },
      ],
    },
    {
      key: 'notes_row',
      title: '6. Comentarios y notas',
      flat: true,
      columns: 1,
      fields: [
        {
          key: 'comments',
          label: 'Comentarios',
          type: 'textarea',
          rows: 2,
          placeholder: 'Comentarios visibles en la orden',
        },
        {
          key: 'notes',
          label: 'Notas',
          type: 'textarea',
          rows: 2,
          placeholder: 'Notas u observaciones internas',
        },
      ],
    },
  ];

  const footerActions = [
    {
      key: 'cancel',
      label: 'Cancelar',
      variant: 'secondary',
      onClick: onClose,
      disabled: isSaving,
    },
    {
      key: 'emit',
      label: isSaving ? 'Guardando...' : 'Emitir',
      variant: 'success',
      onClick: onEmit,
      disabled: isSaving,
    },
    {
      key: 'save',
      label: isSaving ? 'Guardando...' : 'Guardar',
      variant: 'primary',
      onClick: onSave,
      disabled: isSaving,
    },
  ];

  return (
    <>
    <FormDrawer
      isOpen={isOpen}
      onClose={onClose}
      onSave={onSave}
      isSaving={isSaving}
      isDarkMode={isDarkMode}
      title={isEditing ? 'Editar orden de compra' : 'Nueva orden de compra'}
      subtitle="Adquisiciones"
      sections={sections}
      formData={formData}
      onChange={onChange}
      formError={formError}
      width={1180}
      compact
      footerActions={footerActions}
    />
    <FormDrawer
      isOpen={showCatalogForm}
      onClose={() => !isSavingCatalog && setShowCatalogForm(false)}
      onSave={handleSaveCatalogItem}
      isSaving={isSavingCatalog}
      isDarkMode={isDarkMode}
      title="Nuevo ítem catálogo"
      subtitle="Se guarda en el catálogo de adquisiciones y se agrega a esta OC."
      sections={[
        {
          key: 'main',
          title: 'Datos del ítem',
          flat: true,
          columns: 2,
          fields: [
            {
              key: 'item_type',
              label: 'Tipo',
              type: 'select',
              options: [
                { value: '', label: 'Seleccionar tipo' },
                ...catalogTypeOptions,
              ],
            },
            {
              key: 'item_code',
              label: 'Código',
              placeholder: 'Vacío = se asigna automáticamente',
            },
            {
              key: 'description',
              label: 'Item',
              placeholder: 'Nombre del ítem',
            },
            {
              key: 'unit',
              label: 'Formato',
              type: 'select',
              options: [
                { value: '', label: 'Seleccionar formato' },
                ...(formatOptions || []).map((opt) => ({
                  value: opt.value || opt.label,
                  label: opt.label || opt.value,
                })),
                ...(catalogForm.unit &&
                !(formatOptions || []).some(
                  (opt) => opt.value === catalogForm.unit || opt.label === catalogForm.unit
                )
                  ? [{ value: catalogForm.unit, label: catalogForm.unit }]
                  : []),
              ],
            },
            {
              key: 'unit_price',
              label: 'Precio referencial',
              type: 'number',
            },
          ],
        },
      ]}
      formData={catalogForm}
      onChange={handleCatalogFormChange}
      formError={catalogError}
      saveLabel="Crear ítem"
      width={520}
      zIndex={11050}
    />
    </>
  );
};

export default PurchaseOrdersForm;
